import dayjs, { type Dayjs, type ManipulateType } from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import updateLocale from "dayjs/plugin/updateLocale";
import utc from "dayjs/plugin/utc";
import weekOfYear from "dayjs/plugin/weekOfYear";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrBefore);
dayjs.extend(weekOfYear);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);

export const BASE_DATE_FORMAT = "MMM DD, YYYY";

export const DATE_FORMATS = {
  base: BASE_DATE_FORMAT,
} as const;

export interface DateRange {
  from: Dayjs;
  to: Dayjs;
}

function assertPositiveInteger(value: number, label: string) {
  if (!Number.isInteger(value) || value < 1) {
    throw new RangeError(`${label} must be a positive integer.`);
  }
}

export const getLastNDaysDateRange = (
  dayCount: number,
  referenceDate: Dayjs = dayjs.tz(),
): DateRange => {
  assertPositiveInteger(dayCount, "dayCount");

  return {
    from: referenceDate.subtract(dayCount - 1, "day").startOf("day"),
    to: referenceDate.endOf("day"),
  };
};

export const getLastNCompletedMonthsDateRange = (
  monthCount: number,
  referenceDate: Dayjs = dayjs.tz(),
): DateRange => {
  assertPositiveInteger(monthCount, "monthCount");

  return {
    from: referenceDate.subtract(monthCount, "month").startOf("month"),
    to: referenceDate.subtract(1, "month").endOf("month"),
  };
};

export const generateDateArray = (
  startDate: Dayjs,
  endDate: Dayjs,
  unit: "day" | "week" | "month",
): Dayjs[] => {
  const dates: Dayjs[] = [];
  let currentDate = startDate;

  if (unit === "week") {
    currentDate = currentDate.startOf("week");
  } else if (unit === "month") {
    currentDate = currentDate.startOf("month");
  }

  while (currentDate.isSameOrBefore(endDate)) {
    dates.push(currentDate);
    currentDate = currentDate.add(1, unit);
  }

  return dates;
};

export const getDateFormatByDateUnit = (
  value: string,
  dateUnit: "day" | "week" | "month",
) => {
  switch (dateUnit) {
    case "day":
      return dayjs(value).format("MMM DD");
    case "week":
      return `${dayjs(value).format("MMM DD")} - ${dayjs(value).add(6, "day").format("MMM DD")}`;
    case "month":
      return dayjs(value).format("MMM YYYY");
    default:
      return value;
  }
};

export const getTimezoneDayjs = (date?: string | number | Dayjs | null) => {
  if (!date) return dayjs.tz();
  if (dayjs.isDayjs(date)) return date;

  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return dayjs.tz(`${date}T00:00:00`);
  }

  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2} \d{2}$/.test(date)) {
    return dayjs.tz(`${date}:00:00`);
  }

  return dayjs.utc(date).tz();
};

export const setDefaultTimezone = (timezoneName?: string | null) => {
  dayjs.tz.setDefault(timezoneName || undefined);
};

export { dayjs };
export type { Dayjs, ManipulateType };
export default dayjs;
